import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateTools,
  Utils,
  Patterns,
  Input,
  Select,
  Divider,
  DatePicker,
  NumberInput,
  RadioList,
} from '@treezor/design-system';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';

import { CustomColumns, TwoColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { SirenInput } from '@/features/user/components/CreateUser/SirenInput';
import { UserContentLabel } from '@/features/user/components/contentLayout';
import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';
import { IUser, legalForms } from '@/features/user/store/user.model';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { UserTools } from '@/utils/tools/UserTools';

import { useUpdateUser } from './useUpdateUser';

interface IBusinessUserInformationProps {
  user: IUser;
  isMainInformationOnEdition: boolean;
  isContactOnEdition: boolean;
}

export function useBusinessUserInformationProps({
  user,
  isMainInformationOnEdition,
  isContactOnEdition,
}: IBusinessUserInformationProps) {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const { countryCodeOptions, legalFormOptions, legalSectorTypeOptions } = useFieldOptions();

  const distributionCountry = config?.settings?.users?.distributionCountry;
  const hasAddress: boolean = !!(user.address1 || user.address2 || user.postcode || user.city || user.country);

  const {
    onUpdateUser,
    handleValueChange,
    register,
    handleInputChange,
    isLoading,
    watch,
    touchedFields,
    inputMaxLength,
    handleDateChange,
    hasInvalidInputLength,
    language,
    handlePrefillData,
    onCancelEdition,
  } = useUpdateUser(user);

  const hasDistributionCountry = distributionCountry?.length > 0;

  const emailWatch = watch('email');
  const distributionCountryWatch = watch('distributionCountry');
  const phoneWatch = watch('phone');
  const mobileWatch = watch('mobile');
  const address1Watch = watch('address1');
  const address2Watch = watch('address2');
  const address3Watch = watch('address3');
  const legalRegistrationNumberWatch = watch('legalRegistrationNumber');
  const legalRegistrationDateWatch = watch('legalRegistrationDate');
  const selectedlegalRegistrationDate = DateTools.localStringToDate(legalRegistrationDateWatch, language);
  const countryWatch = watch('country');
  const legalSectorTypeWatch = watch('legalSectorType');
  const legalSectorWatch = watch('legalSector');

  const {
    iconStatus,
    error,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch, isRequired: true });

  const emailError = useMemo(() => (user.email !== emailWatch ? error : undefined), [emailWatch, error, user.email]);

  const isMainInformationInvalid = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      (hasDistributionCountry && !distributionCountryWatch) ||
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      !legalRegistrationNumberWatch ||
      !UserTools.validateIntLegalNumber(legalRegistrationNumberWatch) ||
      (!!legalSectorWatch && !Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)),
    [
      emailWatch,
      emailError,
      hasDistributionCountry,
      distributionCountryWatch,
      hasInvalidInputLength,
      legalRegistrationNumberWatch,
      legalSectorTypeWatch,
      legalSectorWatch,
    ],
  );

  const isContactInvalid = useMemo(
    () => (!!mobileWatch && !Patterns.phone.test(mobileWatch)) || (!!phoneWatch && !Patterns.phone.test(phoneWatch)),
    [mobileWatch, phoneWatch],
  );

  const mainInformation = useMemo(
    () =>
      ({
        subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
        ...(isMainInformationOnEdition
          ? {
              component: (
                <div className="flex flex-col gap-4 w-[600px]">
                  <div className="flex flex-col gap-3">
                    <CustomColumns columns={[1, 3]}>
                      <UserContentLabel>{t('pages.dashboard.createUser.form.email')}</UserContentLabel>
                      <Input
                        type="text"
                        icon={{ left: 'mail', right: iconStatus }}
                        label=""
                        required
                        {...register('email', { pattern: Patterns.email })}
                        error={emailError}
                        onBlur={handleEmailBlur}
                        size="s"
                        testId="input-kyc-email"
                      />
                    </CustomColumns>
                    {hasDistributionCountry && (
                      <CustomColumns columns={[1, 3]}>
                        <UserContentLabel>
                          {t('pages.currentProfiles.profile.informations.distributionCountry')}
                        </UserContentLabel>
                        <Select
                          required
                          withSearch
                          options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                          defaultValue={user?.distributionCountry}
                          noOptionsComponent={<CreateUserEmptyComponent />}
                          size="s"
                          onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
                          testId="select-kyc-distribution-country"
                        />
                      </CustomColumns>
                    )}
                  </div>

                  <Divider thickness="200" key={`section-divider-information`} />

                  <SirenInput
                    value={legalRegistrationNumberWatch}
                    hasEntered={true}
                    label={t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber')}
                    required
                    handlePrefillData={handlePrefillData}
                    {...register('legalRegistrationNumber')}
                  >
                    <Input
                      {...register('legalName')}
                      type="text"
                      label={t('pages.dashboard.createUser.form.legalName')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'legalName')
                      }
                      size="s"
                      testId="input-kyc-legal-name"
                    />
                    <CustomColumns columns={[1, 1]}>
                      <DatePicker
                        selectedDate={selectedlegalRegistrationDate}
                        label={t('pages.dashboard.createUser.form.legalRegistrationDate')}
                        todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                        clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                        onChangeDate={(value) => handleDateChange(value, 'legalRegistrationDate')}
                        language={language}
                        size="s"
                        testId="date-picker-kyc-legal-registration"
                      />
                      <Select
                        label={t('pages.dashboard.createUser.form.legalForm')}
                        options={legalFormOptions(distributionCountryWatch)}
                        withSearch
                        defaultValue={user?.legalForm}
                        noOptionsComponent={<CreateUserEmptyComponent />}
                        onChange={(value: string) => handleValueChange(value, 'legalForm')}
                        showDelete
                        size="s"
                        testId="select-kyc-legal-form"
                      />
                    </CustomColumns>

                    <CustomColumns columns={[1, 1]}>
                      <Input
                        type="text"
                        {...register('legalTvaNumber')}
                        label={t('pages.dashboard.createUser.form.legalTvaNumber')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                          handleInputChange(event, 'legalTvaNumber');
                        }}
                        size="s"
                        testId="input-kyc-legal-tva-number"
                      />

                      <NumberInput
                        name="legalShareCapital"
                        defaultValue={user?.legalShareCapital}
                        label={t('pages.dashboard.createUser.form.legalShareCapital')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'legalShareCapital')
                        }
                        unit={
                          <div className="flex gap-3 items-center h-full">
                            <Divider direction="vertical" />
                            <div className="text-xs w-8 min-w-8 text-primary">EUR (€)</div>
                          </div>
                        }
                        size="s"
                        testId="input-kyc-legal-share-capital"
                      />
                    </CustomColumns>
                  </SirenInput>

                  <Divider thickness="200" key={`section-divider-siren`} />

                  <div className="flex flex-col gap-2">
                    <h6 className="typo-h6-bold text-secondary">
                      {t('pages.dashboard.createUser.business.sections.businessAddress')}
                    </h6>
                    <Select
                      label={t('pages.currentProfiles.profile.contact.country')}
                      options={countryCodeOptions}
                      withSearch
                      defaultValue={countryWatch}
                      onChange={(value: string) => {
                        handleValueChange(value, 'country');
                      }}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      showDelete
                      size="s"
                      testId="select-kyc-country"
                    />

                    <TwoColumns>
                      <Input
                        value={address1Watch}
                        type="text"
                        label={t('pages.dashboard.createUser.form.address1')}
                        {...register('address1')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'address1')
                        }
                        {...inputMaxLength(address1Watch, 35)}
                        size="s"
                        testId="input-kyc-address1"
                      />

                      <Input
                        value={address2Watch}
                        type="text"
                        label={t('pages.dashboard.createUser.form.address2')}
                        {...register('address2')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'address2')
                        }
                        {...inputMaxLength(address2Watch, 35)}
                        size="s"
                        testId="input-kyc-address2"
                      />
                    </TwoColumns>
                    <Input
                      value={address3Watch}
                      type="text"
                      label={t('pages.dashboard.createUser.form.address3')}
                      {...register('address3')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'address3')
                      }
                      {...inputMaxLength(address3Watch, 35)}
                      size="s"
                      testId="input-kyc-address2"
                    />
                    <TwoColumns>
                      <Input
                        type="text"
                        label={t('pages.currentProfiles.profile.contact.postcode')}
                        {...register('postcode')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'postcode')
                        }
                        size="s"
                        testId="input-kyc-postcode"
                      />
                      <Input
                        type="text"
                        label={t('pages.currentProfiles.profile.contact.city')}
                        {...register('city')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'city')
                        }
                        size="s"
                        testId="input-kyc-city"
                      />
                    </TwoColumns>
                  </div>

                  <Divider thickness="200" key={`section-divider-address`} />

                  <div className="flex flex-col gap-2">
                    <h6 className="typo-h6-bold text-secondary">
                      {t('pages.dashboard.createUser.business.sections.companyActivities')}
                    </h6>

                    <TwoColumns>
                      <RadioList
                        label={t('pages.dashboard.createUser.form.legalSectorType')}
                        options={legalSectorTypeOptions}
                        value={user?.legalSectorType || legalSectorTypeWatch}
                        onChange={(value: string) => handleValueChange(value, 'legalSectorType')}
                        className="pt-2 gap-3"
                      />
                      <Input
                        type="text"
                        label={t('pages.dashboard.createUser.form.legalSectorNafApe')}
                        {...register('legalSector')}
                        error={
                          legalSectorWatch === '' || Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)
                            ? undefined
                            : legalSectorTypeWatch === 'NACE'
                              ? t('pages.dashboard.createUser.form.rules.incorrectNaceLegalSector')
                              : t('pages.dashboard.createUser.form.rules.incorrectLegalSector')
                        }
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'legalSector')
                        }
                        defaultValue={user?.legalSector}
                        size="s"
                        testId="input-kyc-legal-sector"
                      />
                    </TwoColumns>
                  </div>
                </div>
              ),
            }
          : {
              categories: [
                {
                  items: [
                    { label: t('pages.dashboard.createUser.form.email'), value: user?.email },
                    distributionCountry?.length > 0 && {
                      label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                      value: t(`shared.countries.${user.distributionCountry}`),
                    },
                  ],
                },
                {
                  items: [
                    {
                      label: t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber'),
                      value: user?.legalRegistrationNumber,
                    },
                    { label: t('pages.dashboard.createUser.form.legalName'), value: user?.legalName || '/' },
                    {
                      label: t('pages.dashboard.createUser.form.legalRegistrationDate'),
                      value: user?.legalRegistrationDate || '/',
                    },
                    {
                      label: t('pages.dashboard.createUser.form.legalForm'),
                      value: legalForms?.[user?.legalForm] || '/',
                    },
                    { label: t('pages.dashboard.createUser.form.legalTvaNumber'), value: user?.legalTvaNumber || '/' },
                    {
                      label: t('pages.dashboard.createUser.form.legalShareCapital'),
                      value: user?.legalShareCapital ? `${user?.legalShareCapital}` : '/',
                    },
                  ],
                },
                {
                  component: (
                    <>
                      <div className="flex flex-col gap-2 typo-body-regular-paragraph font-bold">
                        <p className="text-tertiary">{t('pages.currentProfiles.profile.contact.address')}</p>
                        {hasAddress ? (
                          <div>
                            <p>{user.address1 || t('pages.dashboard.createUser.form.noAddress')}</p>
                            <p>{user.address2}</p>
                            <p>{user.address3}</p>
                            <p>
                              {user.postcode || t('pages.dashboard.createUser.form.noPostcode')}{' '}
                              {user.city || t('pages.dashboard.createUser.form.noCity')}
                            </p>
                            <p>
                              {user.country
                                ? t(`shared.countries.${user.country}`)
                                : t('pages.dashboard.createUser.form.noCountry')}
                            </p>
                          </div>
                        ) : (
                          '/'
                        )}
                      </div>
                    </>
                  ),
                },
                {
                  items: [
                    {
                      label: t('pages.dashboard.createUser.form.legalSector'),
                      value: `${user?.legalSectorType} - ${user?.legalSector}`,
                    },
                  ],
                },
              ],
            }),
      }) as TKycSection,
    [
      address1Watch,
      address2Watch,
      address3Watch,
      countryCodeOptions,
      countryWatch,
      distributionCountry,
      emailError,
      handleDateChange,
      handleEmailBlur,
      handleInputChange,
      handlePrefillData,
      handleValueChange,
      hasAddress,
      iconStatus,
      inputMaxLength,
      isMainInformationOnEdition,
      language,
      legalFormOptions,
      legalRegistrationNumberWatch,
      legalSectorTypeOptions,
      legalSectorTypeWatch,
      legalSectorWatch,
      register,
      selectedlegalRegistrationDate,
      t,
      hasDistributionCountry,
      user.address1,
      user.address2,
      user.address3,
      user.city,
      user.country,
      user.distributionCountry,
      user?.email,
      user?.legalForm,
      user?.legalName,
      user?.legalRegistrationDate,
      user?.legalRegistrationNumber,
      user?.legalSector,
      user?.legalSectorType,
      user?.legalShareCapital,
      user?.legalTvaNumber,
      user.postcode,
      distributionCountryWatch,
    ],
  );

  const contact: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
      ...(isContactOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-3">
                <CustomColumns columns={[1, 2]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.phone')}</UserContentLabel>
                  <Contact
                    label=""
                    value={phoneWatch}
                    initialValue={user?.phone}
                    initialCountry={!touchedFields?.['phone'] && !phoneWatch ? distributionCountryWatch : undefined}
                    handleValueChange={handleValueChange}
                    size="s"
                    testId="input-kyc-phone"
                    {...register('phone')}
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 2]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.mobile')}</UserContentLabel>
                  <Contact
                    label=""
                    value={mobileWatch}
                    initialValue={user?.mobile}
                    initialCountry={!touchedFields?.['mobile'] && !mobileWatch ? distributionCountryWatch : undefined}
                    handleValueChange={handleValueChange}
                    size="s"
                    testId="input-kyc-mobile"
                    {...register('mobile')}
                  />
                </CustomColumns>
              </div>
            ),
          }
        : {
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '/' },
                  { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '/' },
                ],
              },
            ],
          }),
    }),
    [
      distributionCountryWatch,
      handleValueChange,
      isContactOnEdition,
      mobileWatch,
      phoneWatch,
      register,
      t,
      touchedFields,
      user?.mobile,
      user?.phone,
    ],
  );

  return {
    mainInformation,
    isMainInformationInvalid,
    isLoading,
    isContactInvalid,
    contact,
    onUpdateUser,
    onCancelEdition,
  };
}
